import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
  KeyTermFieldFragment,
  KeyTermsSectionFragment,
  ParticipantRole,
  ParticipantsFragment,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { InputFormatType } from '@common/enums';
import { getDarwinFieldMappings } from '@common/external/darwin/darwin-field-mappings';
import { DarwinEnabledNormalizedFields } from '@common/external/darwin/darwin.fields';
import { ValueType } from '@common/graphql';
import { formatToCurrency } from '@common/utils/formatter';
import dayjs from 'dayjs';
import { formatUnderscoreField } from '~/common/utils/formatter';
import { formatPercentage } from '~/common/utils/percentage';
import {
  getErrorAndSuccessCountsForIngestionDocument,
  hasDocumentBeenValidated,
  ValidationItem,
} from './validation.utils';

export type KeyTermValue = string | number | boolean | string[] | undefined;

export type ValidationResult = {
  documentCategory?: string;
  documentForm?: string;
  documentName?: string;
  requiredDocument?: {
    isMissing?: boolean;
    isRequired?: boolean;
    message?: string;
  };
};

/**
 * Helper function to get missing document message from validation result
 */
export const getMissingDocumentMessage = (
  result: ValidationResult
):
  | {
      mainLabel: string;
      subLabel?: string;
    }
  | undefined => {
  const isOrMissingDocs =
    result.documentCategory?.includes('OR:') ||
    result.documentForm?.includes('OR:') ||
    result.documentName?.includes('OR:');

  const formsLabel = result.documentForm
    ?.replace('OR:', '')
    ?.split('|')
    ?.join(', ');

  if (isOrMissingDocs) {
    if (result.documentCategory) {
      const categories = result.documentCategory
        .replace('OR:', '')
        .split('|')
        .map((category) => formatUnderscoreField(category));

      return {
        mainLabel:
          categories.length > 1 ? categories.join(', ') : categories[0],
      };
    }

    if (result.documentName) {
      const mainLabel = result.documentName
        .replace('OR:', '')
        .split('|')
        .join(', ');

      return {
        mainLabel,
        subLabel: formsLabel,
      };
    }

    return {
      mainLabel: formsLabel as string,
    };
  }

  if (result.documentName) {
    const mainLabel = result.documentName
      .replace('OR:', '')
      .split('|')
      .join(', ');

    return {
      mainLabel,
      subLabel: formsLabel,
    };
  }

  if (result.documentForm) {
    return {
      mainLabel: formsLabel as string,
    };
  }
  if (result.documentCategory) {
    return {
      mainLabel: formatUnderscoreField(result.documentCategory),
    };
  }

  return undefined;
};

/**
 * Sort function for validation results: errors first, then successes, then date, then name
 */
const sortByValidationResults = (
  a: ExternalIngestionDocumentFragment,
  b: ExternalIngestionDocumentFragment,
  validationData: ExternalValidationReportDataFragment | null
) => {
  // Get validation counts for sorting (more efficient than full results)
  const aCounts = getErrorAndSuccessCountsForIngestionDocument(
    a,
    validationData
  );
  const bCounts = getErrorAndSuccessCountsForIngestionDocument(
    b,
    validationData
  );

  // Sort by errors first (most errors first)
  if (aCounts.errors !== bCounts.errors) {
    return bCounts.errors - aCounts.errors;
  }

  // Then by successes (most successes first)
  if (aCounts.successes !== bCounts.successes) {
    return bCounts.successes - aCounts.successes;
  }

  // Then by updated date descending (most recent first)
  const aUpdated = a.documentUpdatedAt
    ? new Date(a.documentUpdatedAt as string)
    : new Date(a.documentCreatedAt as string);
  const bUpdated = b.documentUpdatedAt
    ? new Date(b.documentUpdatedAt as string)
    : new Date(b.documentCreatedAt as string);

  if (aUpdated.getTime() !== bUpdated.getTime()) {
    return bUpdated.getTime() - aUpdated.getTime();
  }

  // Finally sort by name
  return a.documentName.localeCompare(b.documentName);
};

/**
 * Sort function for document name (alphabetical)
 */
const sortByDocumentName = (
  a: ExternalIngestionDocumentFragment,
  b: ExternalIngestionDocumentFragment
) => {
  const aName = a.documentName || '';
  const bName = b.documentName || '';

  return aName.localeCompare(bName);
};

export type FolderGroup = {
  folderId: string;
  folderName: string;
  activeDocuments: ExternalIngestionDocumentFragment[];
  archivedDocuments: ExternalIngestionDocumentFragment[];
};

export type GroupedDocumentsByFolder = {
  needsReviewDocuments: ExternalIngestionDocumentFragment[];
  folderGroups: FolderGroup[];
};

/**
 * Groups ingestion documents by folder, with all documents placed in their respective folders.
 * Documents are grouped by their folder ID and name, with further separation
 * between active and archived documents within each folder.
 *
 * @param sortedIngestionDocuments - Array of ingestion documents to group
 * @param validationData - Validation data used for sorting documents within groups
 * @param submittedDocumentReviews - Optional array of submitted document reviews
 * @returns Object containing needsReview documents and folder groups (each with active and archived documents)
 */
export const groupIngestionDocumentsByFolder = (
  sortedIngestionDocuments: ExternalIngestionDocumentFragment[],
  validationData: ExternalValidationReportDataFragment | null,
  submittedDocumentReviews?: IngestionDocumentReviewFragment[]
): GroupedDocumentsByFolder => {
  const needsReviewDocuments: ExternalIngestionDocumentFragment[] = [];
  const folderGroupsMap = new Map<string, FolderGroup>();

  // Create a map of submitted reviews by document id for quick lookup
  const submittedReviewsByDocId = new Map<
    string,
    IngestionDocumentReviewFragment
  >();
  submittedDocumentReviews?.forEach((review) => {
    submittedReviewsByDocId.set(review.ingestionDocument.id, review);
  });

  // Group documents
  sortedIngestionDocuments.forEach((document) => {
    // Track needs review documents separately for easy filtering
    if (document.needsReview && !document.isArchived) {
      needsReviewDocuments.push(document);
    }

    // ALL documents go into folder groups (including needs review)
    const folderId = document.folderId || 'unknown';
    const folderName = document.folderName || 'Unknown Folder';

    if (!folderGroupsMap.has(folderId)) {
      folderGroupsMap.set(folderId, {
        folderId,
        folderName,
        activeDocuments: [],
        archivedDocuments: [],
      });
    }

    const folderGroup = folderGroupsMap.get(folderId)!;
    if (document.isArchived) {
      folderGroup.archivedDocuments.push(document);
    } else {
      folderGroup.activeDocuments.push(document);
    }
  });

  // Sort documents within each group
  const sortedFolderGroups = Array.from(folderGroupsMap.values()).map(
    (group) => ({
      ...group,
      activeDocuments: group.activeDocuments.sort((a, b) =>
        sortByValidationResults(a, b, validationData)
      ),
      archivedDocuments: group.archivedDocuments.sort(sortByDocumentName),
    })
  );

  // Sort folder groups: fully archived folders last, then by latest updated document
  sortedFolderGroups.sort((a, b) => {
    const aIsFullyArchived = a.activeDocuments.length === 0;
    const bIsFullyArchived = b.activeDocuments.length === 0;

    // Fully archived folders go to the end
    if (aIsFullyArchived && !bIsFullyArchived) {
      return 1;
    }
    if (!aIsFullyArchived && bIsFullyArchived) {
      return -1;
    }

    // For folders in the same group (both archived or both have active docs),
    // sort by latest updated document in the folder
    const getLatestUpdatedTime = (group: FolderGroup) => {
      const allDocs = [...group.activeDocuments, ...group.archivedDocuments];
      if (allDocs.length === 0) return 0;

      return Math.max(
        ...allDocs.map((doc) => {
          const updated = doc.documentUpdatedAt
            ? new Date(doc.documentUpdatedAt as string)
            : new Date(doc.documentCreatedAt as string);

          return updated.getTime();
        })
      );
    };

    const aLatest = getLatestUpdatedTime(a);
    const bLatest = getLatestUpdatedTime(b);

    // Sort by latest updated time descending (most recent first)
    return bLatest - aLatest;
  });

  return {
    needsReviewDocuments: needsReviewDocuments.sort((a, b) =>
      sortByValidationResults(a, b, validationData)
    ),
    folderGroups: sortedFolderGroups,
  };
};

export const groupIngestionDocuments = (
  sortedIngestionDocuments: ExternalIngestionDocumentFragment[],
  validationData: ExternalValidationReportDataFragment | null,
  submittedDocumentReviews?: IngestionDocumentReviewFragment[]
) => {
  const needsReviewDocuments: ExternalIngestionDocumentFragment[] = [];
  const submittedReviewDocuments: ExternalIngestionDocumentFragment[] = [];
  const otherDocuments: ExternalIngestionDocumentFragment[] = [];
  const archivedDocuments: ExternalIngestionDocumentFragment[] = [];
  const uncheckedDocuments: ExternalIngestionDocumentFragment[] = [];
  const blankDocuments: ExternalIngestionDocumentFragment[] = [];

  // Create a map of submitted reviews by document id for quick lookup
  const submittedReviewsByDocId = new Map<
    string,
    IngestionDocumentReviewFragment
  >();
  submittedDocumentReviews?.forEach((review) => {
    submittedReviewsByDocId.set(review.ingestionDocument.id, review);
  });

  // Group documents by their status
  sortedIngestionDocuments.forEach((document) => {
    const hasValidations = hasDocumentBeenValidated(document, validationData);
    const hasSubmittedReview = submittedReviewsByDocId.has(document.id);

    // isArchived takes precedence over all other statuses
    if (document.isArchived) {
      archivedDocuments.push(document);
    } else if (document.needsReview) {
      // Document needs review - goes to needs review regardless of submitted review status
      needsReviewDocuments.push(document);
    } else if (hasSubmittedReview) {
      // Document has submitted review but doesn't need review - goes to submitted review group
      submittedReviewDocuments.push(document);
    } else if (document.isBlank) {
      blankDocuments.push(document);
    } else if (hasValidations) {
      otherDocuments.push(document);
    } else {
      uncheckedDocuments.push(document);
    }
  });

  return {
    needsReviewDocuments: needsReviewDocuments.sort((a, b) =>
      sortByValidationResults(a, b, validationData)
    ),
    submittedReviewDocuments: submittedReviewDocuments.sort((a, b) =>
      sortByValidationResults(a, b, validationData)
    ),
    otherDocuments: otherDocuments.sort((a, b) =>
      sortByValidationResults(a, b, validationData)
    ),
    archivedDocuments: archivedDocuments.sort(sortByDocumentName),
    uncheckedDocuments: uncheckedDocuments.sort(sortByDocumentName),
    blankDocuments: blankDocuments.sort(sortByDocumentName),
  };
};

/**
 * Legacy function for formatting individual key term values
 * Used by existing table components
 *
 * @param value - The raw value to format (string, number, boolean, array, or undefined)
 * @param fieldName - The field name used to determine formatting rules
 * @returns Formatted string representation of the value
 *
 * @example
 * formatKeyTermValue(250000, 'purchase_price') // Returns "$250,000"
 * formatKeyTermValue('2024-01-15', 'close_date') // Returns "Jan 15, 2024"
 * formatKeyTermValue(null, 'any_field') // Returns "N/A"
 */
export const formatKeyTermValue = (
  value: KeyTermValue,
  fieldName: string
): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // Format currency fields using formatToCurrency helper
  if (
    fieldName.includes('price') ||
    fieldName.includes('fee') ||
    fieldName.includes('deposit') ||
    fieldName.includes('concession') ||
    fieldName.includes('flat')
  ) {
    const formattedCurrency = formatToCurrency(value as number, 0);

    return formattedCurrency || '-';
  }

  // Format date fields using dayjs
  if (fieldName.includes('date')) {
    const date = dayjs(value as string);
    if (date.isValid()) {
      return date.format('MMM DD, YYYY');
    }

    return '-';
  }

  if (fieldName.includes('pct') || fieldName.includes('percent')) {
    const percentValue = Number(value);
    if (!isNaN(percentValue)) {
      return formatPercentage(percentValue, 2, {
        treatAsFraction: false,
      });
    }

    return '-';
  }

  return String(value);
};

/**
 * Helper function to format hierarchical key term field value
 */
const formatHierarchicalKeyTermValue = (
  field: KeyTermFieldFragment
): string => {
  // Handle null/undefined/empty values from Value scalar
  if (field.value === null || field.value === undefined || field.value === '') {
    return 'N/A';
  }

  const parsedValue = field.value as KeyTermValue;

  // If no fieldFormat, return unformatted value
  if (!field.fieldFormat) {
    return String(parsedValue);
  }

  // Parse fieldFormat JSON and format based on type
  try {
    const inputFormat = field.fieldFormat;

    const formatType = inputFormat.type;

    switch (formatType) {
      case InputFormatType.CURRENCY:
        const currencyValue = Number(parsedValue);
        if (!isNaN(currencyValue)) {
          return formatToCurrency(currencyValue, 0) || String(parsedValue);
        }
        break;

      case InputFormatType.DATE:
        const date = dayjs(parsedValue as string);
        if (date.isValid()) {
          return date.format('MMM DD, YYYY');
        }
        break;

      case InputFormatType.NUMBER:
        const numberValue = Number(parsedValue);
        if (!isNaN(numberValue)) {
          return numberValue.toLocaleString();
        }
        break;

      case InputFormatType.PERCENTAGE: {
        const percentValue = Number(parsedValue);
        if (!isNaN(percentValue)) {
          const precision =
            typeof inputFormat.options?.precision === 'number'
              ? inputFormat.options.precision
              : 2;

          return formatPercentage(percentValue, precision, {
            treatAsFraction: false,
          });
        }
        break;
      }
    }
  } catch {
    // If fieldFormat is not valid JSON, return unformatted
  }

  return String(parsedValue);
};

export interface KeyTermGroup {
  title?: string;
  description?: string;
  fields: Array<{ label: string; value: string; field: KeyTermFieldFragment }>;
}

/**
 * Extracts key term values (including overrides) from KeyTermsSectionFragment[]
 * into a flat record structure compatible with Darwin field mapping
 */
export const extractKeyTermValues = (
  keyTerms: KeyTermsSectionFragment[] | undefined
): Record<string, ValueType> => {
  if (!keyTerms || keyTerms.length === 0) {
    return {};
  }

  const parentSection = keyTerms[0];
  if (!parentSection) {
    return {};
  }

  const values: Record<string, ValueType> = {};

  // Extract parent section field values
  if (parentSection.fields && parentSection.fields.length > 0) {
    parentSection.fields.forEach((field) => {
      values[field.key] = field.value as ValueType;
    });
  }

  // Extract child section field values
  if (parentSection.children && parentSection.children.length > 0) {
    parentSection.children.forEach((childSection) => {
      if (childSection.fields && childSection.fields.length > 0) {
        childSection.fields.forEach((field) => {
          values[field.key] = field.value as ValueType;
        });
      }
    });
  }

  return values;
};

export const getGroupedKeyTermsToDisplay = (
  keyTerms: KeyTermsSectionFragment[] | undefined
): KeyTermGroup[] => {
  if (!keyTerms || keyTerms.length === 0) {
    return [
      {
        title: 'Key Terms',
        description: 'Contract key terms and values',
        fields: [
          {
            label: 'Purchase price',
            value: 'N/A',
            field: {
              key: DarwinEnabledNormalizedFields.PURCHASE_PRICE,
              fieldLabel: getDarwinFieldMappings(
                DarwinEnabledNormalizedFields.PURCHASE_PRICE
              )[0].label,
              fieldType: getDarwinFieldMappings(
                DarwinEnabledNormalizedFields.PURCHASE_PRICE
              )[0].fieldType,
              values: [],
              fieldFormat: { type: InputFormatType.CURRENCY, options: {} },
              isEditable: true,
            },
          },
          {
            label: 'Closing date',
            value: 'N/A',
            field: {
              key: DarwinEnabledNormalizedFields.CLOSE_DATE,
              fieldLabel: getDarwinFieldMappings(
                DarwinEnabledNormalizedFields.CLOSE_DATE
              )[0].label,
              fieldType: getDarwinFieldMappings(
                DarwinEnabledNormalizedFields.CLOSE_DATE
              )[0].fieldType,
              values: [],
              isEditable: true,
            },
          },
        ],
      },
    ];
  }

  // Expecting only one parent section with potential children
  const parentSection = keyTerms[0];
  if (!parentSection) {
    return [];
  }

  const groups: KeyTermGroup[] = [];

  // Add parent section fields as the main group
  if (parentSection.fields && parentSection.fields.length > 0) {
    const parentFields = parentSection.fields.map((field) => ({
      label: field.fieldLabel,
      value: formatHierarchicalKeyTermValue(field),
      field,
    }));

    if (parentFields.length > 0) {
      groups.push({
        title: parentSection.title,
        description: parentSection.description,
        fields: parentFields,
      });
    }
  }

  // Add child sections as additional groups
  if (parentSection.children && parentSection.children.length > 0) {
    parentSection.children.forEach((childSection) => {
      if (childSection.fields && childSection.fields.length > 0) {
        const childFields = childSection.fields.map((field) => ({
          label: field.fieldLabel,
          value: formatHierarchicalKeyTermValue(field),
          field,
        }));

        if (childFields.length > 0) {
          groups.push({
            title: childSection.title,
            description: childSection.description,
            fields: childFields,
          });
        }
      }
    });
  }

  return groups;
};

/**
 * Formats compliance status by splitting underscores and capitalizing only the first letter
 * Example: "needs_review" becomes "Needs review"
 */

export function formatComplianceStatus(status: string): string {
  if (!status) return status;

  const words = status.split('_');

  return words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
}

/**
 * Helper function to get participant information to display
 */
export const getParticipantInfo = (
  participants?: ParticipantsFragment
): Array<{ label: string; value: string }> => {
  if (!participants) {
    return [];
  }

  const result: Array<{ label: string; value: string }> = [];

  // Buyers Agents
  if (participants.buyersAgents && participants.buyersAgents.length > 0) {
    result.push({
      label: 'Buyers Agent',
      value: participants.buyersAgents.map((agent) => agent.name).join(', '),
    });

    // Add office name as separate row if any agents have office names
    const officeNames = participants.buyersAgents
      .map((agent) => agent.officeName)
      .filter(Boolean)
      .join(', ');

    if (officeNames) {
      result.push({
        label: 'Office',
        value: officeNames,
      });
    }
  }

  // Listing Agents
  if (participants.listingAgents && participants.listingAgents.length > 0) {
    result.push({
      label: 'Listing Agent',
      value: participants.listingAgents.map((agent) => agent.name).join(', '),
    });

    // Add office name as separate row if any agents have office names
    const officeNames = participants.listingAgents
      .map((agent) => agent.officeName)
      .filter(Boolean)
      .join(', ');

    if (officeNames) {
      result.push({
        label: 'Office',
        value: officeNames,
      });
    }
  }

  // Buyers
  if (participants.buyers && participants.buyers.length > 0) {
    result.push({
      label: 'Buyer',
      value: participants.buyers.map((buyer) => buyer.name).join(', '),
    });
  }

  // Sellers
  if (participants.sellers && participants.sellers.length > 0) {
    result.push({
      label: 'Seller',
      value: participants.sellers.map((seller) => seller.name).join(', '),
    });
  }

  return result;
};

/**
 * Helper function to get participant information for Darwin sync with role mapping
 */
export const getDarwinSyncableParticipants = (
  participants?: ParticipantsFragment,
  transactionSide?: TransactionSide
): Array<{ label: string; value: string; role: ParticipantRole }> => {
  if (!participants) {
    return [];
  }

  const result: Array<{ label: string; value: string; role: ParticipantRole }> =
    [];

  // Buyer agents are supported for Darwin sync
  if (participants.buyersAgents && participants.buyersAgents.length > 0) {
    result.push({
      label: 'Buyer Agent',
      value: participants.buyersAgents.map((agent) => agent.name).join(', '),
      role: ParticipantRole.BUYERS_AGENT,
    });
  }

  // Listing agents are supported for Darwin sync on BUY-side deals (as cobroke agents)
  if (
    transactionSide === TransactionSide.BUY &&
    participants.listingAgents &&
    participants.listingAgents.length > 0
  ) {
    result.push({
      label: 'Listing Agent',
      value: participants.listingAgents.map((agent) => agent.name).join(', '),
      role: ParticipantRole.LISTING_AGENT,
    });
  }

  if (
    (transactionSide === TransactionSide.BUY ||
      transactionSide === TransactionSide.SELL) &&
    participants.buyers &&
    participants.buyers.length > 0
  ) {
    result.push({
      label: 'Buyer',
      value: participants.buyers.map((buyer) => buyer.name).join(', '),
      role: ParticipantRole.BUYER,
    });
  }

  if (
    (transactionSide === TransactionSide.BUY ||
      transactionSide === TransactionSide.SELL) &&
    participants.sellers &&
    participants.sellers.length > 0
  ) {
    result.push({
      label: 'Seller',
      value: participants.sellers.map((seller) => seller.name).join(', '),
      role: ParticipantRole.SELLER,
    });
  }

  return result;
};

/**
 * Calculate visible issues count for a contract document
 * Takes into account hidden validation errors and user-added issues
 *
 * @param contractErrors - Validation errors for the contract document
 * @param userAddedIssues - User-added issues for the contract document
 * @param contractDocId - Contract document ID for matching hidden reviews
 * @param hiddenDocumentValidationReviews - Array of hidden validation reviews
 * @returns Number of visible issues (errors - hidden + user added)
 */
export const getVisibleIssuesCount = (
  contractErrors: ValidationItem[],
  userAddedIssues: IngestionDocumentValidationErrorReviewFragment[],
  contractDocId: string,
  hiddenDocumentValidationReviews: IngestionDocumentValidationErrorReviewFragment[]
): number => {
  // Count hidden validation errors for this contract document
  const hiddenErrorsCount = contractErrors.filter((error) =>
    hiddenDocumentValidationReviews.some(
      (review) =>
        review.ruleUuid === error.ruleUuid &&
        review.contractDocumentUuid === contractDocId &&
        review.contractDocumentUuid === error.contractDocumentUuid &&
        review.ingestionDocumentValidation?.id ===
          error.ingestionDocumentValidationId &&
        review.isHidden
    )
  ).length;

  // Calculate visible count: (total errors - hidden errors) + user added issues
  const visibleErrorsCount = contractErrors.length - hiddenErrorsCount;
  const totalVisibleIssues = visibleErrorsCount + userAddedIssues.length;

  return totalVisibleIssues;
};
